import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Text } from '@components/base/display';
import { Button } from '@components/base/actions';
import { CheckboxGroup } from '@components/base/inputs';
import { Card, HStack, VStack } from '@components/base/layout';
import { Alert, Spinner, useToast } from '@components/base/feedback';
import { usersService } from '../../api/usersService';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * The three global RBAC role slugs seeded by Origami's
 * `RolesAndPermissionsSeeder`. There is no admin endpoint to list these
 * dynamically; the seed is the source of truth and v1 hardcodes them.
 */
const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'operator', label: 'Operator' },
  { value: 'viewer', label: 'Viewer' },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface UserRolesPanelProps {
  readonly userId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function sortedKey(values: readonly string[]): string {
  return [...values].sort().join(',');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UserRolesPanel({ userId }: UserRolesPanelProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Same key as `UserFormPage`'s `useAdminFormPage` query — TanStack
  // dedupes so we don't issue a second GET.
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users', userId],
    queryFn: () => usersService.get(userId),
  });

  const serverRoles = useMemo<string[]>(
    () => data?.data?.roles ?? [],
    [data],
  );

  const [pendingRoles, setPendingRoles] = useState<string[]>(serverRoles);
  const serverRolesKey = sortedKey(serverRoles);

  // Reset local pending state whenever the server roles change
  // (initial fetch or post-mutation invalidation). We compare by
  // sorted-join to avoid resetting when array identity changes but
  // contents don't.
  useEffect(() => {
    setPendingRoles(serverRoles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverRolesKey]);

  const mutation = useMutation({
    mutationFn: (roles: string[]) => usersService.syncRoles(userId, roles),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'users', userId],
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast({ variant: 'success', title: 'Roles updated successfully.' });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: 'Failed to update roles',
        message:
          err instanceof Error ? err.message : 'An unexpected error occurred.',
      });
    },
  });

  const hasChanges = sortedKey(pendingRoles) !== serverRolesKey;

  const handleSave = () => {
    mutation.mutate(pendingRoles);
  };

  // ---- Render ----

  return (
    <Card>
      <Card.Header title="Global Roles" />
      <Card.Content>
        {isLoading && !data ? (
          <VStack align="center" justify="center" gap={2} className="py-4">
            <Spinner size="md" />
            <Text size="sm" color="muted">
              Loading roles...
            </Text>
          </VStack>
        ) : error ? (
          <Alert variant="error" title="Failed to load roles">
            {error instanceof Error
              ? error.message
              : 'An unexpected error occurred while loading roles.'}
          </Alert>
        ) : (
          <VStack gap={4}>
            <CheckboxGroup
              options={ROLE_OPTIONS}
              value={pendingRoles}
              onChange={setPendingRoles}
            />
            <Text size="sm" color="muted">
              Global RBAC roles grant permissions across all tenants. The
              &apos;role&apos; field on the Tenants tab is unrelated
              descriptive metadata.
            </Text>
            {mutation.isError && (
              <Alert variant="error" title="Failed to update roles">
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : 'An unexpected error occurred.'}
              </Alert>
            )}
            <HStack justify="end">
              <Button
                variant="primary"
                onClick={handleSave}
                loading={mutation.isPending}
                disabled={!hasChanges}
              >
                Save Roles
              </Button>
            </HStack>
          </VStack>
        )}
      </Card.Content>
    </Card>
  );
}
